import { config } from "@config/config";
import { useQueryClient } from "@tanstack/react-query";
import { isNewSyncStamp, manualSyncStartFailure } from "@utils/ehrActions";
import { isSyncDone, learningTreeReviewVariables, useLearningTreeReviewEnabled } from "@utils/learningTreeReview";
import { useCallback, useEffect, useRef } from "react";

import {
  ApiLearnerManualGoalSyncStatusChoices,
  useLearnerManualGoalSyncByIdQuery,
  useStartManualSyncWorkflowMutation,
} from "@api/graphql/types-and-hooks";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { useEHRConnection } from "@providers/EHRConnectionProvider";
import { useReport } from "@providers/ReportProvider";

const ACTIVE_SYNC_STATUSES = new Set([
  ApiLearnerManualGoalSyncStatusChoices.Inprogress,
  ApiLearnerManualGoalSyncStatusChoices.Pending,
]);

/** Shared hook for refresh-programming (EHR sync). Use in GoalsSyncButton and in sidebar PopoverMenu "Refresh programming". */
export const useManualRefreshProgramming = () => {
  const { report } = useReport();
  const learnerId = report?.learner?.id ?? "";
  const reportId = report?.id ?? "";

  const { isOrgUsernamePassword, ehrConnectionStatus, orgEhrUnusable, noCredentials, isEhrSyncCleared } =
    useEHRConnection();

  const queryClient = useQueryClient();
  // The server's lastUpdated when this hook first saw the learner's sync status,
  // then the stamp of the last Completed it acted on. Goals are only invalidated
  // for a Completed with a different stamp, so a Completed left from an earlier
  // run, including one the next refresh's start answers with, never triggers a
  // false refresh.
  const syncStampBaselineRef = useRef<string | null | undefined>(undefined);

  const {
    data,
    refetch: refetchStatus,
    isPending: isManualSyncStatusPending,
  } = useLearnerManualGoalSyncByIdQuery(
    { learnerId },
    {
      enabled: !!learnerId,
      refetchInterval: (query) => {
        const status = query.state?.data?.learnerById?.manualGoalSyncStatus;
        if (ACTIVE_SYNC_STATUSES.has(status as ApiLearnerManualGoalSyncStatusChoices)) {
          return config.REPORT_POLLING_INTERVAL;
        }
        return false;
      },
    },
  );
  // Tracks whether a sync was started from this hook in this session, so the
  // failure effect below only reacts to an attempt the user just made — not a
  // leftover Failure status already present on mount.
  const syncStartedThisSessionRef = useRef<boolean>(false);

  const { mutate: startManualSyncWorkflowMutation, isPending: isStartManualSyncWorkflowPending } =
    useStartManualSyncWorkflowMutation({
      onSuccess: async (data) => {
        const startFailure = manualSyncStartFailure(data.startManualSyncWorkflow);
        if (startFailure) {
          // Most refusals are about the EHR connection (session expired, no
          // credentials, org session not ready), so refresh what the badge shows.
          // Some also write a fresh Failure to the learner, so read it once.
          syncStartedThisSessionRef.current = false;
          notifyError(startFailure);
          void queryClient.invalidateQueries({ queryKey: ["ehrConnection"] });
          void queryClient.invalidateQueries({ queryKey: ["getHasEhrCredentials"] });
          await refetchStatus();
          return;
        }
        notifySuccess("Import started successfully");
        await refetchStatus();
      },
      onError: () => {
        notifyError("Failed to start import");
      },
    });

  const hasSyncStatus = data !== undefined;
  const syncStatus = data?.learnerById?.manualGoalSyncStatus;
  const lastUpdated = data?.learnerById?.manualGoalSyncLastUpdated;

  const isSyncing =
    isManualSyncStatusPending ||
    isStartManualSyncWorkflowPending ||
    ACTIVE_SYNC_STATUSES.has(syncStatus as ApiLearnerManualGoalSyncStatusChoices);

  const learningTreeReview = useLearningTreeReviewEnabled();

  const refresh = useCallback(() => {
    // Managed-onboarding gate: an uncleared tenant can't trigger an EHR re-sync
    // (the backend rejects it too). Guarded here so no call site can bypass it.
    if (!learnerId || !reportId || isSyncing || orgEhrUnusable || !isEhrSyncCleared) return;
    syncStartedThisSessionRef.current = true;
    startManualSyncWorkflowMutation({
      learnerId,
      reportId,
      reassessmentReportGoalSync: true,
      ...learningTreeReviewVariables(learningTreeReview),
    });
  }, [
    learnerId,
    reportId,
    isSyncing,
    orgEhrUnusable,
    isEhrSyncCleared,
    startManualSyncWorkflowMutation,
    learningTreeReview,
  ]);

  useEffect(() => {
    if (!hasSyncStatus) return;
    if (syncStampBaselineRef.current === undefined) {
      syncStampBaselineRef.current = lastUpdated ?? null;
      return;
    }
    if (isSyncDone(syncStatus) && isNewSyncStamp(lastUpdated, syncStampBaselineRef.current)) {
      syncStampBaselineRef.current = lastUpdated;
      queryClient.invalidateQueries({ queryKey: ["get-reassessment-goals", reportId] });
      // Goal/target progress charts (ShortTermGoalProgressGraph, TargetProgressGraph)
      // read from this separate cache key, keyed by goalId/targetId rather than
      // reportId — without this, a graph already mounted before the refresh keeps
      // showing pre-refresh data (PROA-2123).
      queryClient.invalidateQueries({ queryKey: ["GetSessionTargetProgress"] });
      // A sync re-runs the CentralReach learning-tree mapping, which is
      // IN_PROGRESS by now: refetching the report starts the narrative page's
      // "refreshing" hold (and its polling), then shows the fields it filled.
      void queryClient.invalidateQueries({ queryKey: ["get-report-by-id", reportId] });
    }
  }, [hasSyncStatus, syncStatus, lastUpdated, reportId, queryClient]);

  // Org-level admin account: this flow polls its own sync status, not
  // ehr_connection, so the badge query never starts polling on its own. When a
  // sync started in this session resolves to Failure, refetch the connection so a
  // credential failure surfaces on the top badge without a manual reload —
  // matching ResyncGoals and the reassessment auto-start flow.
  useEffect(() => {
    if (!syncStartedThisSessionRef.current) return;
    if (syncStatus === ApiLearnerManualGoalSyncStatusChoices.Failure) {
      syncStartedThisSessionRef.current = false;
      if (isOrgUsernamePassword) {
        queryClient.invalidateQueries({ queryKey: ["ehrConnection"] });
      }
    } else if (isSyncDone(syncStatus)) {
      syncStartedThisSessionRef.current = false;
    }
  }, [syncStatus, isOrgUsernamePassword, queryClient]);

  return {
    refresh,
    isSyncing,
    lastUpdated,
    syncStatus,
    canRefresh: !!learnerId && !!reportId,
    orgEhrUnusable,
    ehrConnectionStatus,
    noCredentials,
    isEhrSyncCleared,
  };
};
